/**
 * 学習イベント記録と圧縮集計(docs/07_learning_support.md §2 / §4.1 / §5「Phase 1」)。
 *
 * ## 方針
 * - プレイ中は docs/07 §2 の `LearningEvent` をそのままの形でメモリバッファに積む
 *   (`key:f` / `kana:しゃ` を skillTags に載せる)
 * - **生イベントは永続化しない**。セッション終了時に「セッションドキュメント + 累積集計」へ
 *   圧縮して localStorage に書く(Firestore へ1イベント1ドキュメントを書かないのと同じ理由)
 * - 生イベントは `setFlushHook` に渡した関数へ一度だけ流す。将来ここに
 *   Cloud Functions → BigQuery 送信(docs/07 §2 Phase 3)を差し込む
 *
 * ## 永続化される形
 * ```
 * keys:    'f' → { attempts, misses, reactionMs, reactionSamples }
 * kana:    'しゃ' → 同上
 * days:    'YYYY-MM-DD'(ローカル日付) → { sessions, typed, misses, durationMs }  ← 連続日数用
 * sessions: 直近 50 件のセッションドキュメント(古い順)
 * totals:  通算
 * ```
 */

import {
  getSpellings,
  normalizeReading,
  type Difficulty,
  type SessionResult,
} from '@paltyping/engine';
import type { Language } from './content';
import type { KeyValueStorage } from './progress';
import { appStorage, recordUserStateOperation, registerUserStateReload } from './userState';

// ── docs/07 §2 のイベントスキーマ ─────────────────────────

/**
 * イベント種別。docs/07 §2 の共通スキーマに合わせる。
 * タイピングでは `problem_shown`(出題)/ `input`(1打鍵)/ `answer_submit`(1語完成)を使う。
 */
export type LearningEventType =
  'session_start' | 'problem_shown' | 'input' | 'answer_submit' | 'session_end';

/** クライアント→バッファ→(将来)バッチ送信 される1イベント。 */
export interface LearningEvent {
  readonly sessionId: string;
  /** セッション開始からの秒(小数第3位まで)。 */
  readonly t: number;
  readonly type: LearningEventType;
  /** 出題ID(= wordId)。 */
  readonly problemId?: string;
  /** `key:f` / `kana:しゃ` / コンテンツのタグ。 */
  readonly skillTags: readonly string[];
  readonly payload: Readonly<Record<string, unknown>>;
}

// ── 集計スキーマ ──────────────────────────────────────────

/** スキルタグ1つぶんの累積(キー別・かな別で共用)。 */
export interface SkillStat {
  /** 打鍵の試行回数(正解 + ミス)。 */
  readonly attempts: number;
  readonly misses: number;
  /** 正解打鍵の「直前打鍵からの間隔」合計(ms)。 */
  readonly reactionMs: number;
  /** 上の合計に寄与したサンプル数(平均を出すための分母)。 */
  readonly reactionSamples: number;
}

/** 1日ぶんのプレイ記録(連続日数🔥の計算に使う)。 */
export interface DayStat {
  readonly sessions: number;
  readonly typed: number;
  readonly misses: number;
  readonly durationMs: number;
}

/** セッションドキュメント(生イベントを捨てたあとに残る成績サマリ)。 */
export interface SessionDoc {
  readonly sessionId: string;
  /** 終了時刻(epoch ms)。 */
  readonly at: number;
  /** ローカルタイムゾーンでの日付 `YYYY-MM-DD`。 */
  readonly date: string;
  readonly stageId: string;
  readonly stageTitle: string;
  readonly difficulty: Difficulty;
  readonly language: Language;
  /** にがてキー練習など、進捗に残さないモードか。 */
  readonly practice: boolean;
  readonly durationMs: number;
  readonly score: number;
  readonly stars: number;
  readonly cleared: boolean;
  readonly correctKeys: number;
  readonly missKeys: number;
  readonly kpm: number;
  readonly accuracy: number;
  readonly wordsCompleted: number;
}

export interface LearningTotals {
  readonly sessions: number;
  readonly typed: number;
  readonly misses: number;
  readonly durationMs: number;
}

/** localStorage に載る学習データ全体。 */
export interface LearningSnapshot {
  readonly version: number;
  readonly keys: Readonly<Record<string, SkillStat>>;
  readonly kana: Readonly<Record<string, SkillStat>>;
  readonly days: Readonly<Record<string, DayStat>>;
  /** 古い順。末尾が最新。最大 `MAX_SESSION_DOCS` 件。 */
  readonly sessions: readonly SessionDoc[];
  readonly totals: LearningTotals;
}

/** セッション1回ぶんの圧縮結果。 */
export interface SessionAggregate {
  readonly doc: SessionDoc;
  readonly keys: Readonly<Record<string, SkillStat>>;
  readonly kana: Readonly<Record<string, SkillStat>>;
}

/** localStorage 肥大化対策: セッションドキュメントは直近この件数だけ持つ。 */
export const MAX_SESSION_DOCS = 50;
/** 日別記録の保持上限(連続日数はもっと短い範囲しか見ない)。 */
export const MAX_DAY_RECORDS = 400;
/** メモリバッファの上限。長時間プレイでも生イベントで膨らませない。 */
export const MAX_BUFFERED_EVENTS = 4000;
/**
 * 反応時間としてカウントする上限(ms)。
 * これを超える間隔は「考え込み・離席」なので平均反応時間に混ぜない。
 */
export const REACTION_CAP_MS = 5000;

const STORAGE_KEY = 'paltyping.learning.v1';

export const EMPTY_LEARNING: LearningSnapshot = {
  version: 1,
  keys: {},
  kana: {},
  days: {},
  sessions: [],
  totals: { sessions: 0, typed: 0, misses: 0, durationMs: 0 },
};

const EMPTY_STAT: SkillStat = { attempts: 0, misses: 0, reactionMs: 0, reactionSamples: 0 };

// ── 純関数 ────────────────────────────────────────────────

/** ローカルタイムゾーンでの `YYYY-MM-DD`(UTCではない: 深夜プレイが前日に落ちないように)。 */
export function localDateKey(epochMs: number): string {
  const date = new Date(epochMs);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** `YYYY-MM-DD` を `days` 前にずらす(ローカル日付のまま)。 */
export function shiftDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  date.setDate(date.getDate() + days);
  return localDateKey(date.getTime());
}

/**
 * 読みの `index` 文字目から始まるかなユニット。
 * 拗音などの2かなユニット(`しゃ`)はローマ字テーブルに載っているかで判定する。
 */
export function kanaUnitAt(reading: string, index: number): string | null {
  const chars = [...normalizeReading(reading)];
  if (index < 0 || index >= chars.length) return null;
  const first = chars[index] ?? '';
  const second = chars[index + 1];
  if (second !== undefined && getSpellings(first + second) !== undefined) return first + second;
  return first;
}

/**
 * かな集計の対象にしてよいユニットか。
 *
 * ホームポジション練習のように読みが ASCII(`ffff` など)のステージでは
 * 「かな」ユニットがただの英字になってしまうので、キー集計と二重に持たない。
 */
export function isKanaUnit(unit: string): boolean {
  return [...unit].every((char) => (char.codePointAt(0) ?? 0) > 0x7f);
}

function bump(
  target: Record<string, SkillStat>,
  id: string,
  correct: boolean,
  elapsedMs: number | null,
): void {
  const current = target[id] ?? EMPTY_STAT;
  const counted = correct && elapsedMs !== null && elapsedMs >= 0 && elapsedMs <= REACTION_CAP_MS;
  target[id] = {
    attempts: current.attempts + 1,
    misses: current.misses + (correct ? 0 : 1),
    reactionMs: current.reactionMs + (counted ? (elapsedMs ?? 0) : 0),
    reactionSamples: current.reactionSamples + (counted ? 1 : 0),
  };
}

function tagValue(tag: string, prefix: string): string | null {
  if (!tag.startsWith(`${prefix}:`)) return null;
  const value = tag.slice(prefix.length + 1);
  return value.length === 0 ? null : value;
}

/**
 * 生イベント列 → キー別・かな別の集計(圧縮の本体)。
 *
 * `input` イベントの `skillTags`(`key:*` / `kana:*`)と
 * `payload.correct` / `payload.elapsedMs` だけを見る純関数。
 */
export function compressEvents(
  events: readonly LearningEvent[],
  doc: SessionDoc,
): SessionAggregate {
  const keys: Record<string, SkillStat> = {};
  const kana: Record<string, SkillStat> = {};
  for (const event of events) {
    if (event.type !== 'input') continue;
    const correct = event.payload.correct === true;
    const raw = event.payload.elapsedMs;
    const elapsedMs = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
    for (const tag of event.skillTags) {
      const keyId = tagValue(tag, 'key');
      if (keyId !== null) {
        bump(keys, keyId, correct, elapsedMs);
        continue;
      }
      const kanaId = tagValue(tag, 'kana');
      if (kanaId !== null) bump(kana, kanaId, correct, elapsedMs);
    }
  }
  return { doc, keys, kana };
}

function mergeStats(
  base: Readonly<Record<string, SkillStat>>,
  add: Readonly<Record<string, SkillStat>>,
): Record<string, SkillStat> {
  const merged: Record<string, SkillStat> = { ...base };
  for (const [id, stat] of Object.entries(add)) {
    const current = merged[id] ?? EMPTY_STAT;
    merged[id] = {
      attempts: current.attempts + stat.attempts,
      misses: current.misses + stat.misses,
      reactionMs: current.reactionMs + stat.reactionMs,
      reactionSamples: current.reactionSamples + stat.reactionSamples,
    };
  }
  return merged;
}

/** セッション集計を累積へ畳み込む(純関数)。上限件数の間引きもここで行う。 */
export function mergeSessionAggregate(
  snapshot: LearningSnapshot,
  aggregate: SessionAggregate,
): LearningSnapshot {
  const { doc } = aggregate;

  const today = snapshot.days[doc.date] ?? { sessions: 0, typed: 0, misses: 0, durationMs: 0 };
  const days: Record<string, DayStat> = {
    ...snapshot.days,
    [doc.date]: {
      sessions: today.sessions + 1,
      typed: today.typed + doc.correctKeys,
      misses: today.misses + doc.missKeys,
      durationMs: today.durationMs + doc.durationMs,
    },
  };
  const dayKeys = Object.keys(days).sort();
  if (dayKeys.length > MAX_DAY_RECORDS) {
    for (const key of dayKeys.slice(0, dayKeys.length - MAX_DAY_RECORDS)) delete days[key];
  }

  return {
    version: 1,
    keys: mergeStats(snapshot.keys, aggregate.keys),
    kana: mergeStats(snapshot.kana, aggregate.kana),
    days,
    sessions: [...snapshot.sessions, doc].slice(-MAX_SESSION_DOCS),
    totals: {
      sessions: snapshot.totals.sessions + 1,
      typed: snapshot.totals.typed + doc.correctKeys,
      misses: snapshot.totals.misses + doc.missKeys,
      durationMs: snapshot.totals.durationMs + doc.durationMs,
    },
  };
}

/**
 * 連続プレイ日数🔥。
 *
 * 今日プレイ済みなら今日から、まだなら昨日から遡って数える
 * (「今日まだ遊んでいない」だけで記録が 0 に見えると心が折れるため)。
 */
export function currentStreak(
  days: Readonly<Record<string, DayStat>>,
  nowMs: number = Date.now(),
): number {
  const today = localDateKey(nowMs);
  let cursor = days[today] ? today : shiftDateKey(today, -1);
  let streak = 0;
  while (days[cursor]) {
    streak += 1;
    cursor = shiftDateKey(cursor, -1);
  }
  return streak;
}

// ── 「できるようになったこと」(docs/07 §4.2 生徒ビュー)──

/** ★が増えたステージ(直近のセッションから検出)。 */
export interface StarGain {
  readonly stageId: string;
  readonly stageTitle: string;
  readonly difficulty: Difficulty;
  readonly stars: number;
  readonly previousStars: number;
  readonly at: number;
}

/** はやさの伸び。 */
export interface SpeedGrowth {
  readonly recentKpm: number;
  readonly earlierKpm: number;
  readonly deltaKpm: number;
}

export interface Achievements {
  readonly starGains: readonly StarGain[];
  readonly speed: SpeedGrowth | null;
  readonly bestKpm: number;
}

/** 伸びの比較に使う直近セッション数。 */
const SPEED_WINDOW = 5;

/**
 * 「できるようになったこと」を組み立てる。
 *
 * - ★が増えたステージ: 保存済みセッションを古い順に走査し、同じステージ×難易度の
 *   自己ベストを更新した回を拾う(進捗スナップショットに依存せず自己完結する)
 * - はやさの伸び: 直近5セッションの平均 kpm と、その前5セッションの平均 kpm を比べる
 */
export function selectAchievements(snapshot: LearningSnapshot, limit = 3): Achievements {
  const best = new Map<string, number>();
  const gains: StarGain[] = [];
  for (const doc of snapshot.sessions) {
    if (doc.practice) continue;
    const key = `${doc.stageId}:${doc.difficulty}`;
    const previous = best.get(key) ?? 0;
    if (doc.stars > previous) {
      best.set(key, doc.stars);
      gains.push({
        stageId: doc.stageId,
        stageTitle: doc.stageTitle,
        difficulty: doc.difficulty,
        stars: doc.stars,
        previousStars: previous,
        at: doc.at,
      });
    }
  }

  const played = snapshot.sessions.filter((doc) => doc.correctKeys > 0);
  const recent = played.slice(-SPEED_WINDOW);
  const earlier = played.slice(-SPEED_WINDOW * 2, -SPEED_WINDOW);
  const average = (docs: readonly SessionDoc[]): number =>
    docs.length === 0 ? 0 : docs.reduce((sum, doc) => sum + doc.kpm, 0) / docs.length;
  const speed: SpeedGrowth | null =
    recent.length > 0 && earlier.length > 0
      ? {
          recentKpm: Math.round(average(recent)),
          earlierKpm: Math.round(average(earlier)),
          deltaKpm: Math.round(average(recent) - average(earlier)),
        }
      : null;

  return {
    // 新しいものから見せる。
    starGains: gains.reverse().slice(0, limit),
    speed,
    bestKpm: played.reduce((max, doc) => Math.max(max, Math.round(doc.kpm)), 0),
  };
}

// ── 永続化 ────────────────────────────────────────────────

function parseStat(raw: unknown): SkillStat | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Partial<SkillStat>;
  const num = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
  const attempts = num(record.attempts);
  if (attempts <= 0) return null;
  return {
    attempts,
    misses: Math.min(attempts, num(record.misses)),
    reactionMs: num(record.reactionMs),
    reactionSamples: num(record.reactionSamples),
  };
}

function parseStatMap(raw: unknown): Record<string, SkillStat> {
  const result: Record<string, SkillStat> = {};
  if (typeof raw !== 'object' || raw === null) return result;
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    const stat = parseStat(value);
    if (stat) result[id] = stat;
  }
  return result;
}

/** 壊れたJSON・古いスキーマを捨てて既定値に落とす(progress.ts と同じ方針)。 */
export function deserializeLearning(raw: string | null): LearningSnapshot {
  if (!raw) return EMPTY_LEARNING;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_LEARNING;
    const record = parsed as Partial<LearningSnapshot>;
    if (record.version !== 1) return EMPTY_LEARNING;

    const days: Record<string, DayStat> = {};
    if (typeof record.days === 'object' && record.days !== null) {
      for (const [date, value] of Object.entries(record.days as Record<string, unknown>)) {
        if (typeof value !== 'object' || value === null) continue;
        const day = value as Partial<DayStat>;
        days[date] = {
          sessions: Number(day.sessions) || 0,
          typed: Number(day.typed) || 0,
          misses: Number(day.misses) || 0,
          durationMs: Number(day.durationMs) || 0,
        };
      }
    }

    const sessions = Array.isArray(record.sessions)
      ? (
          record.sessions.filter((doc) => typeof doc === 'object' && doc !== null) as SessionDoc[]
        ).slice(-MAX_SESSION_DOCS)
      : [];

    const totals = (record.totals ?? {}) as Partial<LearningTotals>;

    return {
      version: 1,
      keys: parseStatMap(record.keys),
      kana: parseStatMap(record.kana),
      days,
      sessions,
      totals: {
        sessions: Number(totals.sessions) || 0,
        typed: Number(totals.typed) || 0,
        misses: Number(totals.misses) || 0,
        durationMs: Number(totals.durationMs) || 0,
      },
    };
  } catch {
    return EMPTY_LEARNING;
  }
}

// ── 記録 ──────────────────────────────────────────────────

/** セッションのメタ情報(プレイ開始時に決まるもの)。 */
export interface SessionMeta {
  readonly stageId: string;
  readonly stageTitle: string;
  readonly difficulty: Difficulty;
  readonly language: Language;
  /** 進捗に残さない練習モードか。 */
  readonly practice?: boolean;
}

/** `bridge/playController` から呼ばれる記録口。 */
export interface LearningSink {
  start(at: number): void;
  wordStart(input: {
    readonly at: number;
    readonly wordId?: string | undefined;
    readonly reading: string;
    readonly tags?: readonly string[] | undefined;
  }): void;
  key(input: {
    readonly at: number;
    /** 実際に押されたキー。 */
    readonly key: string;
    /** ガイドが示していた次のキー(ミスの帰属先)。 */
    readonly expected: string;
    readonly accepted: boolean;
    /** 打鍵直前の読みの確定位置(かなユニットの特定に使う)。 */
    readonly unitIndex: number;
  }): void;
  wordCompleted(input: { readonly at: number; readonly wordId?: string | undefined }): void;
  end(input: { readonly at: number; readonly result: SessionResult }): void;
}

/**
 * 将来の送信口(docs/07 §2「詳細分析用の生イベントは Cloud Functions 経由で BigQuery 等へ」)。
 * ここに `httpsCallable(functions, 'ingestLearningEvents')` を差すだけで拡張できる。
 */
export type LearningFlushHook = (
  events: readonly LearningEvent[],
  aggregate: SessionAggregate,
) => void;

/** 何も記録しないシンク(テスト・記録OFF用)。 */
export const NULL_SINK: LearningSink = {
  start: () => undefined,
  wordStart: () => undefined,
  key: () => undefined,
  wordCompleted: () => undefined,
  end: () => undefined,
};

export interface LearningRecorderOptions {
  readonly storage?: KeyValueStorage | null;
  readonly key?: string;
  /** 壁時計(セッションドキュメントの日付用)。 */
  readonly now?: () => number;
  readonly newSessionId?: () => string;
}

/**
 * 学習イベントの記録係。
 *
 * `sink(meta)` で1プレイぶんの記録口を作り、`end()` で圧縮 → localStorage 反映 →
 * フラッシュフック呼び出し、までを行う。
 */
export class LearningRecorder {
  private snapshot: LearningSnapshot | null = null;
  private flushHook: LearningFlushHook | null = null;
  private sequence = 0;

  private readonly storage: KeyValueStorage | null;
  private readonly storageKey: string;
  private readonly now: () => number;
  private readonly newSessionId: () => string;

  constructor(options: LearningRecorderOptions = {}) {
    this.storage = options.storage === undefined ? appStorage() : options.storage;
    this.storageKey = options.key ?? STORAGE_KEY;
    this.now = options.now ?? (() => Date.now());
    this.newSessionId =
      options.newSessionId ??
      (() => `s_${this.now().toString(36)}_${(this.sequence += 1).toString(36)}`);
  }

  /** 現在の学習データ。 */
  load(): LearningSnapshot {
    this.snapshot ??= deserializeLearning(this.storage?.getItem(this.storageKey) ?? null);
    return this.snapshot;
  }

  /** 次の `load()` で localStorage を読み直す(読み戻しのあと。docs/04 §5.1.1)。 */
  reload(): void {
    this.snapshot = null;
  }

  reset(): LearningSnapshot {
    this.snapshot = EMPTY_LEARNING;
    this.storage?.removeItem(this.storageKey);
    return this.snapshot;
  }

  /** 生イベントの送り先(Phase 3 の BigQuery シンク)。null で解除。 */
  setFlushHook(hook: LearningFlushHook | null): void {
    this.flushHook = hook;
  }

  /** 1プレイぶんの記録口を作る。 */
  sink(meta: SessionMeta): LearningSink {
    return new RecordingSession(this, meta, this.newSessionId());
  }

  /** @internal `RecordingSession` から呼ばれる。 */
  commit(events: readonly LearningEvent[], doc: SessionDoc): SessionAggregate {
    const aggregate = compressEvents(events, doc);
    this.snapshot = mergeSessionAggregate(this.load(), aggregate);
    try {
      this.storage?.setItem(this.storageKey, JSON.stringify(this.snapshot));
    } catch {
      // 容量超過・プライベートモード等。記録が残らないだけでプレイは続行できる。
    }
    // セッションIDを操作IDにする。同じ終了通知の再送・通信の応答喪失では、
    // サーバー側の台帳が一度だけ集計する。
    recordUserStateOperation({
      id: `learning:${aggregate.doc.sessionId}`,
      section: 'learning',
      occurredAt: aggregate.doc.at,
      payload: { aggregate: aggregate as unknown as Record<string, unknown> },
    });
    this.flushHook?.(events, aggregate);
    return aggregate;
  }

  /** @internal */
  wallClock(): number {
    return this.now();
  }
}

class RecordingSession implements LearningSink {
  private readonly events: LearningEvent[] = [];
  private startedAt = 0;
  private lastKeyAt = 0;
  private ended = false;
  private current: {
    readonly id: string | undefined;
    readonly reading: string;
    readonly tags: readonly string[];
  } | null = null;

  constructor(
    private readonly recorder: LearningRecorder,
    private readonly meta: SessionMeta,
    private readonly sessionId: string,
  ) {}

  start(at: number): void {
    this.startedAt = at;
    this.lastKeyAt = at;
    this.push(at, 'session_start', undefined, [`stage:${this.meta.stageId}`], {
      difficulty: this.meta.difficulty,
      language: this.meta.language,
    });
  }

  wordStart(input: {
    readonly at: number;
    readonly wordId?: string | undefined;
    readonly reading: string;
    readonly tags?: readonly string[] | undefined;
  }): void {
    this.current = { id: input.wordId, reading: input.reading, tags: input.tags ?? [] };
    this.lastKeyAt = input.at;
    this.push(input.at, 'problem_shown', input.wordId, this.current.tags, {
      reading: input.reading,
    });
  }

  key(input: {
    readonly at: number;
    readonly key: string;
    readonly expected: string;
    readonly accepted: boolean;
    readonly unitIndex: number;
  }): void {
    // 正解は「押したキー」、ミスは「押すはずだったキー」に帰属させる。
    const attributed = input.accepted ? input.key : input.expected || input.key;
    if (attributed.length === 0) return;
    const tags = [`key:${attributed}`];
    if (this.meta.language === 'japanese' && this.current) {
      const unit = kanaUnitAt(this.current.reading, input.unitIndex);
      if (unit !== null && isKanaUnit(unit)) tags.push(`kana:${unit}`);
    }
    const elapsedMs = Math.max(0, Math.round(input.at - this.lastKeyAt));
    this.lastKeyAt = input.at;
    this.push(input.at, 'input', this.current?.id, tags, {
      key: input.key,
      expected: input.expected,
      correct: input.accepted,
      elapsedMs,
    });
  }

  wordCompleted(input: { readonly at: number; readonly wordId?: string | undefined }): void {
    this.push(input.at, 'answer_submit', input.wordId, this.current?.tags ?? [], { correct: true });
  }

  end(input: { readonly at: number; readonly result: SessionResult }): void {
    if (this.ended) return;
    this.ended = true;
    const { result } = input;
    this.push(input.at, 'session_end', undefined, [`stage:${this.meta.stageId}`], {
      cleared: result.cleared,
      score: result.score,
      stars: result.stars,
    });
    const at = this.recorder.wallClock();
    const doc: SessionDoc = {
      sessionId: this.sessionId,
      at,
      date: localDateKey(at),
      stageId: this.meta.stageId,
      stageTitle: this.meta.stageTitle,
      difficulty: this.meta.difficulty,
      language: this.meta.language,
      practice: this.meta.practice === true,
      durationMs: Math.round(result.durationMs),
      score: Math.floor(result.score),
      stars: result.stars,
      cleared: result.cleared,
      correctKeys: result.correctKeys,
      missKeys: result.missKeys,
      kpm: Math.round(result.kpm * 10) / 10,
      accuracy: Math.round(result.accuracy * 1000) / 1000,
      wordsCompleted: result.completedQuestions,
    };
    // 生イベントは保存しない。切り出してフラッシュに渡し、バッファは捨てる。
    const events = [...this.events];
    this.events.length = 0;
    this.recorder.commit(events, doc);
  }

  private push(
    at: number,
    type: LearningEventType,
    problemId: string | undefined,
    skillTags: readonly string[],
    payload: Readonly<Record<string, unknown>>,
  ): void {
    if (this.events.length >= MAX_BUFFERED_EVENTS) return;
    this.events.push({
      sessionId: this.sessionId,
      t: Math.round(at - this.startedAt) / 1000,
      type,
      ...(problemId === undefined ? {} : { problemId }),
      skillTags,
      payload,
    });
  }
}

/** アプリ全体で使う記録係。 */
export const learningRecorder = new LearningRecorder();

// 読み戻し(docs/04 §5.1.1)のあと、サーバーのマージ結果を読み直せるようにする。
registerUserStateReload(() => learningRecorder.reload());
